import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, map, take } from 'rxjs';
import { LucideAngularModule, Clock, X, Sparkles, Plus, ChevronUp, ChevronDown } from 'lucide-angular';
import { LessonPlanService } from '../../services/lesson-plan.service';
import { THEME_COLOR, LessonBlock } from '../../services/lesson-plan.models';
import { StepSlidesLoadingComponent } from '../step-slides-loading/step-slides-loading';
import { AutoResizeTextareaDirective } from '../../../../../common/directives/auto-resize-textarea.directive';
import { AuthService } from '../../../../../common/auth/auth.service';

@Component({
  selector: 'app-step-structure',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, StepSlidesLoadingComponent, AutoResizeTextareaDirective],
  templateUrl: './step-structure.html',
  styleUrl: './step-structure.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepStructureComponent implements OnInit {
  private readonly lessonPlanService = inject(LessonPlanService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  @Output() readonly back = new EventEmitter<void>();
  @Output() readonly next = new EventEmitter<void>();
  @ViewChild(StepSlidesLoadingComponent) loadingComponent?: StepSlidesLoadingComponent;

  protected readonly themeColor = THEME_COLOR;
  protected readonly planStructure$ = this.lessonPlanService.planStructure$;
  protected readonly error$ = this.lessonPlanService.error$;

  /** Whether we're showing the loading screen */
  private readonly showLoading$$ = new BehaviorSubject<boolean>(false);
  protected readonly showLoading$ = this.showLoading$$.asObservable();

  protected readonly duration$ = this.lessonPlanService.inputData$.pipe(
    map((data) => (data.duration ? parseInt(data.duration, 10) : 0))
  );

  protected readonly totalTime$ = this.planStructure$.pipe(
    map((plan) => plan.reduce((acc, curr) => acc + (curr.duration || 0), 0))
  );

  protected readonly timeInfo$ = combineLatest([this.totalTime$, this.duration$]).pipe(
    map(([total, duration]) => ({
      total,
      duration,
      color:
        total > duration ? 'text-red-500' : total < duration ? 'text-yellow-600' : 'text-green-600',
    }))
  );

  protected readonly icons = {
    Clock,
    X,
    Sparkles,
    Plus,
    ChevronUp,
    ChevronDown,
  };

  ngOnInit(): void {
    // Check if we came back from funnel-auth with generateSlides flag
    this.route.queryParams
      .pipe(take(1))
      .subscribe((params) => {
        if (params['generateSlides'] === 'true') {
          // Clear the query param to prevent re-triggering
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {},
            replaceUrl: true,
          });

          // User returned from auth - assign lesson plan and generate slides
          this.assignAndGenerateSlides();
        }
      });
  }

  /**
   * Assign the lesson plan to the current user and then generate slides
   * Called after returning from funnel-auth
   */
  private assignAndGenerateSlides(): void {
    // First assign the lesson plan to the user
    this.lessonPlanService
      .assignToCurrentUser()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Now generate slides
          this.doGenerateSlides();
        },
        error: (err) => {
          console.error('Failed to assign lesson plan:', err);
          // Still try to generate slides - might already be assigned
          this.doGenerateSlides();
        },
      });
  }

  protected updateBlock(id: number, field: keyof LessonBlock, event: Event): void {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const value = field === 'duration' ? parseInt(target.value, 10) || 0 : target.value;
    this.lessonPlanService.updateBlock(id, field, value);
  }

  protected deleteBlock(id: number): void {
    this.lessonPlanService.deleteBlock(id);
  }

  protected addBlock(): void {
    this.lessonPlanService.addBlock();
  }

  protected moveBlockUp(id: number): void {
    this.lessonPlanService.moveBlockUp(id);
  }

  protected moveBlockDown(id: number): void {
    this.lessonPlanService.moveBlockDown(id);
  }

  protected onBack(): void {
    this.back.emit();
  }

  protected onCreateSlides(): void {
    // Check if user is authenticated
    this.authService
      .isAuthenticated()
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        if (!isAuthenticated) {
          // Not authenticated - redirect to signup page with full redirect URL
          const lessonPlanId = this.lessonPlanService.lessonPlanId;
          const redirectUrl = `/lesson-plan/${lessonPlanId}?generateSlides=true`;
          this.router.navigate(['/sign-up'], {
            queryParams: { redirectUrl, userType: 'teacher' },
          });
          return;
        }

        // User is authenticated - generate slides
        this.doGenerateSlides();
      });
  }

  /**
   * Actually generate slides (called after authentication check passes)
   */
  private doGenerateSlides(): void {
    // Show loading screen
    this.showLoading$$.next(true);

    // Generate slides via API
    this.lessonPlanService
      .confirmStructureAndGenerateSlides()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          // Complete the loading animation
          this.loadingComponent?.complete();

          // Delay to show 100% progress and all steps completed before transitioning
          // 1000ms gives users time to see the completion state
          setTimeout(() => {
            this.next.emit();
          }, 1000);
        },
        error: () => {
          // Error is already handled by the service
          this.showLoading$$.next(false);
        },
      });
  }
}
